import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar, Doughnut, Radar, Scatter, Pie, PolarArea } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Filler,
  Tooltip,
  Legend
);

export const SLDS_CHART_COLORS = ['#0176d3', '#2e844a', '#fe9339', '#c23934', '#9050e9', '#06a59a'];
export const SLDS_CHART_COLORS_ALPHA = SLDS_CHART_COLORS.map(c => c + '33');

const sldsDefaults = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      labels: {
        font: { family: "'Salesforce Sans', Arial, sans-serif", size: 12 },
        color: '#3e3e3c',
        usePointStyle: true,
        padding: 16,
      },
    },
    tooltip: {
      backgroundColor: 'white',
      titleColor: '#080707',
      bodyColor: '#3e3e3c',
      borderColor: '#dddbda',
      borderWidth: 1,
      padding: 12,
      titleFont: { family: "'Salesforce Sans', Arial, sans-serif", weight: '700', size: 13 },
      bodyFont: { family: "'Salesforce Sans', Arial, sans-serif", size: 12 },
      cornerRadius: 4,
      displayColors: true,
      boxPadding: 4,
    },
  },
  scales: {
    x: {
      grid: { color: '#f3f3f3' },
      ticks: { font: { family: "'Salesforce Sans', Arial, sans-serif", size: 11 }, color: '#706e6b' },
    },
    y: {
      grid: { color: '#f3f3f3' },
      ticks: { font: { family: "'Salesforce Sans', Arial, sans-serif", size: 11 }, color: '#706e6b' },
    },
  },
};

const mergeOptions = (overrides = {}) => {
  const merged = JSON.parse(JSON.stringify(sldsDefaults));
  // Deep merge overrides
  const merge = (target, source) => {
    for (const key in source) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        target[key] = target[key] || {};
        merge(target[key], source[key]);
      } else {
        target[key] = source[key];
      }
    }
  };
  merge(merged, overrides);
  return merged;
};

export const SLDSLineChart = ({ data, options = {}, height = 300 }) => (
  <div style={{ height, position: 'relative' }}>
    <Line data={data} options={mergeOptions(options)} />
  </div>
);

export const SLDSBarChart = ({ data, options = {}, height = 300 }) => (
  <div style={{ height, position: 'relative' }}>
    <Bar data={data} options={mergeOptions(options)} />
  </div>
);

export const SLDSDoughnutChart = ({ data, options = {}, height = 300 }) => (
  <div style={{ height, position: 'relative' }}>
    <Doughnut data={data} options={mergeOptions({ ...options, scales: undefined })} />
  </div>
);

export const SLDSRadarChart = ({ data, options = {}, height = 300 }) => (
  <div style={{ height, position: 'relative' }}>
    <Radar data={data} options={mergeOptions({
      ...options,
      scales: {
        r: {
          grid: { color: '#dddbda' },
          angleLines: { color: '#dddbda' },
          ticks: { font: { size: 10 }, color: '#706e6b', backdropColor: 'transparent' },
          pointLabels: { font: { family: "'Salesforce Sans', Arial, sans-serif", size: 11 }, color: '#3e3e3c' },
        },
      },
    })} />
  </div>
);

export const SLDSScatterChart = ({ data, options = {}, height = 300 }) => (
  <div style={{ height, position: 'relative' }}>
    <Scatter data={data} options={mergeOptions(options)} />
  </div>
);

export const SLDSPieChart = ({ data, options = {}, height = 300 }) => (
  <div style={{ height, position: 'relative' }}>
    <Pie data={data} options={mergeOptions({ ...options, scales: undefined })} />
  </div>
);

export const SLDSPolarAreaChart = ({ data, options = {}, height = 300 }) => (
  <div style={{ height, position: 'relative' }}>
    <PolarArea data={data} options={mergeOptions({
      ...options,
      scales: {
        r: {
          grid: { color: '#dddbda' },
          ticks: { font: { size: 10 }, color: '#706e6b', backdropColor: 'transparent' },
        },
      },
    })} />
  </div>
);

export const SparkLine = ({ data, color = '#0176d3', width = 80, height = 30 }) => (
  <div style={{ width, height, display: 'inline-block' }}>
    <Line
      data={{
        labels: data.map((_, i) => i),
        datasets: [{
          data,
          borderColor: color,
          borderWidth: 1.5,
          pointRadius: 0,
          tension: 0.4,
          fill: false,
        }],
      }}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
        scales: {
          x: { display: false },
          y: { display: false },
        },
      }}
    />
  </div>
);

export const MiniDonut = ({ value, total, color = '#0176d3', size = 60 }) => {
  const remainder = total - value;
  return (
    <div style={{ width: size, height: size }}>
      <Doughnut
        data={{
          datasets: [{
            data: [value, remainder],
            backgroundColor: [color, '#f3f3f3'],
            borderWidth: 0,
          }],
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          cutout: '70%',
          plugins: { legend: { display: false }, tooltip: { enabled: false } },
        }}
      />
    </div>
  );
};
